/**
 * DAILY CLOSING REPORTS SERVICE
 * Comprehensive End-of-Day Summary and Sales Reports
 * Supports both daily closing reports and interim sales summaries
 *
 * All revenue / VAT logic must go through the shared `reportingUtils` helpers
 * so the four reporting pages (POS Orders, Admin Reports, Branches, Daily
 * Closing) reconcile to the cent for the same period (Finanzamt / GoBD).
 */
import { isRevenueOrder, aggregateVAT } from '../utils/reportingUtils';

export interface DailyClosingReport {
  id?: string;
  report_date: string;
  shift_start: string;
  shift_end: string;
  cashier_name: string;
  branch: string;
  
  // Financial Summary
  financial_summary: {
    total_sales: number;
    total_orders: number;
    total_customers: number;
    average_order_value: number;
    gross_revenue: number;
    net_revenue: number;
    tax_collected: number;
    discounts_given: number;
    refunds_issued: number;
    split_payment_orders: number;
    split_payment_revenue: number;
    split_payment_percentage: number;
  };
  
  // Payment Methods Breakdown
  payment_methods: {
    cash: { count: number; amount: number; percentage: number };
    card: { count: number; amount: number; percentage: number };
    paypal: { count: number; amount: number; percentage: number };
    other: { count: number; amount: number; percentage: number };
  };
  
  // Cash Reconciliation
  cash_reconciliation: {
    opening_cash: number;
    cash_sales: number;
    cash_refunds: number;
    expected_cash: number;
    actual_cash: number;
    cash_difference: number;
    cash_over_short: number;
  };
  
  // Sales Analysis
  sales_analysis: {
    hourly_breakdown: Array<{
      hour: number;
      orders: number;
      revenue: number;
      average_order_value: number;
    }>;
    top_selling_items: Array<{
      name: string;
      quantity: number;
      revenue: number;
      percentage_of_sales: number;
    }>;
    category_performance: Array<{
      category: string;
      orders: number;
      revenue: number;
      percentage_of_sales: number;
    }>;
  };
  
  // Operational Metrics
  operational_metrics: {
    peak_hours: string[];
    slow_hours: string[];
    busiest_staff_member: string;
    average_service_time: number;
    customer_satisfaction_score: number;
  };
  
  // Employee Performance (NEW: Track each employee separately)
  employee_performance: Array<{
    employee_name: string;
    employee_email: string;
    orders_created: number;
    total_sales: number;
    average_order_value: number;
    hours_worked: number;
    first_order_time: string;
    last_order_time: string;
    payment_methods: {
      cash: number;
      card: number;
      paypal: number;
      other: number;
    };
  }>;
  
  // Audit Trail
  audit_trail: {
    report_generated_at: string;
    generated_by: string;
    last_order_time: string;
    first_order_time: string;
    system_uptime: number;
    errors_logged: number;
  };
  
  // Status
  status: 'draft' | 'final' | 'approved' | 'archived';
  created_at: string;
  updated_at: string;
}

export interface DailyClosingResetEvent {
  id?: string;
  report_id: string;
  report_date: string;
  cashier_name: string;
  branch: string;
  previous_status: string;
  performed_by_name: string;
  performed_by_email?: string;
  reason: string;
  created_at: string;
}

export interface InterimSalesSummary {
  id?: string;
  summary_time: string;
  period_start: string;
  period_end: string;
  cashier_name: string;
  
  // Quick Summary
  quick_summary: {
    current_sales: number;
    current_orders: number;
    hourly_rate: number;
    projected_daily_sales: number;
    time_to_goal: string;
  };
  
  // Recent Activity
  recent_activity: {
    last_hour_orders: number;
    last_hour_revenue: number;
    peak_performance_hour: string;
    current_trend: 'increasing' | 'decreasing' | 'stable';
  };
  
  // Alerts
  alerts: Array<{
    type: 'warning' | 'info' | 'success' | 'error';
    message: string;
    timestamp: string;
  }>;
  
  created_at: string;
}

export interface Zwischenbericht {
  id?: string;
  report_date: string;
  report_time: string;
  cashier_name: string;
  cash_register: string; // e.g., "Kasse 1"
  branch: string;
  
  // Financial Summary
  financial_summary: {
    total_income: number; // Einnahmen
    total_expenses: number; // Ausgaben
    final_balance: number; // Endbestand
  };
  
  // VAT Breakdown
  vat_breakdown: {
    vat_7_percent: {
      gross: number;
      net: number;
      tax: number;
    };
    vat_19_percent: {
      gross: number;
      net: number;
      tax: number;
    };
    total_net: number;
    total_gross: number;
  };
  
  // Cash Register Transactions
  cash_transactions: Array<{
    type: string; // e.g., "Bankeinzahlung"
    amount: number;
  }>;
  
  // Payment Methods Summary
  payment_methods: {
    cash: { count: number; amount: number };
    takeaway_com: { count: number; amount: number };
    ubereats: { count: number; amount: number };
  };
  
  // Sales Channels - Cash Payments
  sales_channels_cash: Array<{
    channel: string; // e.g., "Lieferung"
    payment_method: string; // "bar"
    amount: number;
    count?: number;
  }>;
  
  // Sales Channels - All Payments
  sales_channels_all: Array<{
    channel: string; // e.g., "Lieferung", "Abholung"
    payment_method: string; // e.g., "bar", "Onlinezahlung takeaway.com", "Onlinezahlung UberEats"
    amount: number;
    count?: number;
  }>;
  
  // Bonus System
  bonus_system: {
    points_awarded: number;
    points_redeemed: number;
    amount: number;
  };
  
  created_at: string;
}

class DailyClosingReportsService {
  private static instance: DailyClosingReportsService;

  private constructor() {
    console.log('📊 Daily Closing Reports Service initialized');
  }

  public static getInstance(): DailyClosingReportsService {
    if (!DailyClosingReportsService.instance) {
      DailyClosingReportsService.instance = new DailyClosingReportsService();
    }
    return DailyClosingReportsService.instance;
  }

  /**
   * Generate comprehensive daily closing report
   */
  public async generateDailyClosingReport(
    reportDate: string,
    cashierName: string,
    openingCash: number = 0,
    actualCash: number = 0
  ): Promise<DailyClosingReport> {
    try {
      console.log(`📊 [DAILY CLOSING] Generating daily closing report for ${reportDate}...`);
      
      // Calculate shift times
      const shiftStart = new Date(reportDate);
      shiftStart.setHours(9, 0, 0, 0); // 9:00 AM
      
      const shiftEnd = new Date(reportDate);
      shiftEnd.setHours(23, 59, 59, 999); // 11:59 PM
      
      // Fetch all data for the day - FILTERED BY CASHIER
      // CRITICAL: Only get orders/payments created by this specific cashier
      console.log(`🔍 [DAILY CLOSING] Starting report generation for cashier: "${cashierName}" on date: ${reportDate}`);
      
      const [orders, payments] = await Promise.all([
        this.getOrdersForDate(reportDate, cashierName),
        this.getPaymentsForDate(reportDate, cashierName)
      ]);
      
      console.log(`📊 [DAILY CLOSING] Filtered data for cashier "${cashierName}": ${orders.length} orders, ${payments.length} payments`);
      
      // Log order details for debugging
      if (orders.length > 0) {
        console.log(`📋 [DAILY CLOSING] Orders for cashier "${cashierName}":`, orders.map(o => ({
          id: o.id,
          order_number: o.order_number,
          total: o.total,
          created_by_email: o.created_by_email,
          created_by_name: o.created_by_name,
          created_by: o.created_by
        })));
      } else {
        console.log(`⚠️ [DAILY CLOSING] No orders found for cashier "${cashierName}" on ${reportDate}`);
      }

      // Generate comprehensive report
      const report: Omit<DailyClosingReport, 'id'> = {
        report_date: reportDate,
        shift_start: shiftStart.toISOString(),
        shift_end: shiftEnd.toISOString(),
        cashier_name: cashierName, // This is the person generating the report
        branch: 'saheeh-riyadh',
        
        financial_summary: this.generateFinancialSummary(orders, payments),
        payment_methods: this.generatePaymentMethodsBreakdown(payments),
        cash_reconciliation: this.generateCashReconciliation(orders, payments, openingCash, actualCash),
        sales_analysis: this.generateSalesAnalysis(orders),
        operational_metrics: this.generateOperationalMetrics(orders),
        employee_performance: this.generateEmployeePerformance(orders, payments),
        audit_trail: this.generateAuditTrail(orders),
        
        status: 'draft',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Save report to Firebase
      const savedReport = await this.saveDailyClosingReport(report);
      
      console.log(`✅ [DAILY CLOSING] Daily closing report generated successfully`);
      return savedReport;

    } catch (error) {
      console.error(`❌ [DAILY CLOSING] Failed to generate daily closing report:`, error);
      throw error;
    }
  }

  /**
   * Generate Zwischenbericht (Interim Report) - German cash register format
   */
  public async generateZwischenbericht(
    reportDate: string,
    cashierName: string,
    cashRegister: string = 'Kasse 1'
  ): Promise<Zwischenbericht> {
    try {
      console.log(`📊 [ZWISCHENBERICHT] Generating Zwischenbericht for ${reportDate}...`);
      
      const now = new Date();
      const reportDateObj = new Date(reportDate);
      reportDateObj.setHours(0, 0, 0, 0);
      const endOfDay = new Date(reportDateObj);
      endOfDay.setHours(23, 59, 59, 999);
      
      // Fetch orders and payments for the day - FILTERED BY CASHIER
      const [orders, payments] = await Promise.all([
        this.getOrdersForDate(reportDate, cashierName),
        this.getPaymentsForPeriod(reportDateObj.toISOString(), endOfDay.toISOString(), cashierName)
      ]);
      
      console.log(`📊 [ZWISCHENBERICHT] Found ${orders.length} orders and ${payments.length} payments`);
      
      // Shared revenue filter — same as Daily Closing main path + POS Orders
      // + Admin Reports + Branches. No more divergent local rules.
      const completedOrders = orders.filter(isRevenueOrder);
      
      // CRITICAL FIX: Use orders as the source of truth for total sales (same as Daily Closing Reports)
      // Calculate total sales from orders first to ensure accuracy
      const totalSalesFromOrders = completedOrders.reduce((sum, order) => {
        const orderTotal = typeof order.total === 'number' ? order.total : parseFloat(order.total || '0');
        return sum + (isNaN(orderTotal) ? 0 : orderTotal);
      }, 0);
      
      console.log(`📊 [ZWISCHENBERICHT] Total sales from orders: €${totalSalesFromOrders.toFixed(2)} (${completedOrders.length} orders)`);
      
      // Calculate VAT breakdown (7% and 19%) - ITEM LEVEL CALCULATION
      let vat7Gross = 0;
      let vat19Gross = 0;
      
      // Calculate VAT at item level (each item has its own tax_rate)
      completedOrders.forEach(order => {
        if (order.items && Array.isArray(order.items)) {
          order.items.forEach((item: any) => {
            const itemTotal = typeof item.total === 'number' ? item.total : parseFloat(item.total || '0');
            const taxRate = item.tax_rate || 7; // Default to 7% if not specified
            
            // Group by actual tax rate from item
            if (taxRate === 7 || taxRate === 0.07) {
              vat7Gross += itemTotal;
            } else if (taxRate === 19 || taxRate === 0.19) {
              vat19Gross += itemTotal;
            } else {
              // Fallback: determine by order type if tax_rate not available
              const orderType = (order.order_type || 'dine_in').toLowerCase();
              if (orderType === 'dine_in') {
                vat19Gross += itemTotal;
              } else {
                // Check if item is a drink
                const itemName = (item.menu_item_name || item.name || '').toLowerCase();
                const isDrink = itemName.includes('drink') || itemName.includes('getränk') || 
                               itemName.includes('wasser') || itemName.includes('cola') ||
                               itemName.includes('saft') || itemName.includes('bier');
                if (isDrink) {
                  vat19Gross += itemTotal;
                } else {
                  vat7Gross += itemTotal;
                }
              }
            }
          });
        } else {
          // Fallback: if no items array, use order total with order-level logic
          const orderTotal = typeof order.total === 'number' ? order.total : parseFloat(order.total || '0');
          const orderType = (order.order_type || 'dine_in').toLowerCase();
          if (orderType === 'dine_in') {
            vat19Gross += orderTotal;
          } else {
            vat7Gross += orderTotal;
          }
        }
      });
      
      // Calculate net and tax for each VAT rate (with proper rounding)
      const vat7Net = Math.round((vat7Gross / 1.07) * 100) / 100;
      const vat7Tax = Math.round((vat7Gross - vat7Net) * 100) / 100;
      const vat19Net = Math.round((vat19Gross / 1.19) * 100) / 100;
      const vat19Tax = Math.round((vat19Gross - vat19Net) * 100) / 100;
      
      // Calculate totals (with proper rounding)
      const totalGross = Math.round((vat7Gross + vat19Gross) * 100) / 100;
      const totalNet = Math.round((vat7Net + vat19Net) * 100) / 100;
      
      // Group payment methods by portal - USE ACTUAL PAYMENT RECORDS
      const paymentMethods = {
        cash: { count: 0, amount: 0 },
        takeaway_com: { count: 0, amount: 0 },
        ubereats: { count: 0, amount: 0 }
      };
      
      // Use actual payment records for accurate amounts
      const completedPayments = payments.filter(payment => 
        payment.status === 'completed' || payment.status === 'paid'
      );
      
      completedPayments.forEach(payment => {
        const paymentAmount = typeof payment.amount === 'number' ? payment.amount : parseFloat(payment.amount || '0');
        const paymentMethod = (payment.payment_method || 'cash').toLowerCase();
        const portal = (payment.portal || payment.order?.portal || '').toLowerCase();
        
        if (paymentMethod === 'cash' || paymentMethod === 'bar') {
          paymentMethods.cash.count++;
          paymentMethods.cash.amount += paymentAmount;
        } else if (portal.includes('takeaway') || portal.includes('takeaway.com')) {
          paymentMethods.takeaway_com.count++;
          paymentMethods.takeaway_com.amount += paymentAmount;
        } else if (portal.includes('ubereats') || portal.includes('uber')) {
          paymentMethods.ubereats.count++;
          paymentMethods.ubereats.amount += paymentAmount;
        } else if (paymentMethod !== 'cash' && paymentMethod !== 'bar') {
          // Online payment but portal not specified - default to takeaway.com
          paymentMethods.takeaway_com.count++;
          paymentMethods.takeaway_com.amount += paymentAmount;
        }
      });
      
      // Round payment method amounts
      paymentMethods.cash.amount = Math.round(paymentMethods.cash.amount * 100) / 100;
      paymentMethods.takeaway_com.amount = Math.round(paymentMethods.takeaway_com.amount * 100) / 100;
      paymentMethods.ubereats.amount = Math.round(paymentMethods.ubereats.amount * 100) / 100;
      
      // Sales channels - Cash payments (group by channel) - USE ACTUAL PAYMENT RECORDS
      const cashChannels: Record<string, number> = {};
      completedPayments.forEach(payment => {
        const paymentMethod = (payment.payment_method || 'cash').toLowerCase();
        const orderId = payment.order_id || payment.order?.id;
        const order = completedOrders.find(o => o.id === orderId || o.order_number === orderId);
        
        if (paymentMethod === 'cash' || paymentMethod === 'bar') {
          const paymentAmount = typeof payment.amount === 'number' ? payment.amount : parseFloat(payment.amount || '0');
          const orderType = (order?.order_type || '').toLowerCase();
          let channel = 'Abholung';
          if (orderType === 'delivery' || orderType === 'lieferung') {
            channel = 'Lieferung';
          }
          
          if (!cashChannels[channel]) {
            cashChannels[channel] = 0;
          }
          cashChannels[channel] += paymentAmount;
        }
      });
      
      // Round cash channel amounts
      Object.keys(cashChannels).forEach(channel => {
        cashChannels[channel] = Math.round(cashChannels[channel] * 100) / 100;
      });
      
      // Convert to array format with counts for cash channels
      const salesChannelsCashArray: Array<{ channel: string; payment_method: string; amount: number; count?: number }> = [];
      Object.keys(cashChannels).forEach(channel => {
        // Count cash orders for this channel
        const cashCount = completedOrders.filter(order => {
          const orderType = (order.order_type || '').toLowerCase();
          const paymentMethod = (order.payment_method || 'cash').toLowerCase();
          let orderChannel = 'Abholung';
          if (orderType === 'delivery' || orderType === 'lieferung') {
            orderChannel = 'Lieferung';
          }
          return orderChannel === channel && (paymentMethod === 'cash' || paymentMethod === 'bar');
        }).length;
        
        salesChannelsCashArray.push({
          channel,
          payment_method: 'bar',
          amount: cashChannels[channel],
          count: cashCount
        });
      });
      
      // Sales channels - All payments with proper counting
      const salesChannelsAll: Array<{ channel: string; payment_method: string; amount: number; count?: number }> = [];
      
      // Group by channel and payment method with counts
      const channelGroups: Record<string, Record<string, { amount: number; count: number }>> = {};
      
      // Use actual payment records for accurate sales channel amounts
      completedPayments.forEach(payment => {
        const paymentAmount = typeof payment.amount === 'number' ? payment.amount : parseFloat(payment.amount || '0');
        const orderId = payment.order_id || payment.order?.id;
        const order = completedOrders.find(o => o.id === orderId || o.order_number === orderId);
        
        const orderType = (order?.order_type || 'dine_in').toLowerCase();
        const paymentMethod = (payment.payment_method || 'cash').toLowerCase();
        const portal = (payment.portal || order?.portal || '').toLowerCase();
        
        let channel = 'Abholung'; // Default
        if (orderType === 'delivery' || orderType === 'lieferung') {
          channel = 'Lieferung';
        }
        
        let paymentLabel = 'bar';
        if (paymentMethod === 'cash' || paymentMethod === 'bar') {
          paymentLabel = 'bar';
        } else if (portal.includes('takeaway') || portal.includes('takeaway.com')) {
          paymentLabel = 'Onlinezahlung takeaway.com';
        } else if (portal.includes('ubereats') || portal.includes('uber')) {
          paymentLabel = 'Onlinezahlung UberEats';
        } else {
          paymentLabel = 'Onlinezahlung';
        }
        
        if (!channelGroups[channel]) {
          channelGroups[channel] = {};
        }
        if (!channelGroups[channel][paymentLabel]) {
          channelGroups[channel][paymentLabel] = { amount: 0, count: 0 };
        }
        channelGroups[channel][paymentLabel].amount += paymentAmount;
        channelGroups[channel][paymentLabel].count += 1;
      });
      
      // Round all channel amounts
      Object.keys(channelGroups).forEach(channel => {
        Object.keys(channelGroups[channel]).forEach(paymentLabel => {
          channelGroups[channel][paymentLabel].amount = Math.round(channelGroups[channel][paymentLabel].amount * 100) / 100;
        });
      });
      
      // Convert to array format with counts
      Object.keys(channelGroups).forEach(channel => {
        Object.keys(channelGroups[channel]).forEach(paymentLabel => {
          salesChannelsAll.push({
            channel,
            payment_method: paymentLabel,
            amount: channelGroups[channel][paymentLabel].amount,
            count: channelGroups[channel][paymentLabel].count
          });
        });
      });
      
      // Cash transactions (e.g., Bankeinzahlung)
      // Bank deposit = total cash payments for the day
      const cashTransactions: Array<{ type: string; amount: number }> = [];
      if (paymentMethods.cash.amount > 0) {
        cashTransactions.push({
          type: 'Bankeinzahlung',
          amount: Math.round(paymentMethods.cash.amount * 100) / 100
        });
      }
      
      const zwischenbericht: Omit<Zwischenbericht, 'id'> = {
        report_date: reportDate,
        report_time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        cashier_name: cashierName,
        cash_register: cashRegister,
        branch: orders[0]?.branch || 'saheeh-riyadh',
        
        financial_summary: {
          total_income: totalGross, // Total from all orders (VAT included)
          total_expenses: 0, // Typically 0 for Zwischenbericht
          final_balance: totalGross // Should match total_income
        },
        
        vat_breakdown: {
          vat_7_percent: {
            gross: vat7Gross,
            net: vat7Net,
            tax: vat7Tax
          },
          vat_19_percent: {
            gross: vat19Gross,
            net: vat19Net,
            tax: vat19Tax
          },
          total_net: totalNet,
          total_gross: totalGross
        },
        
        cash_transactions: cashTransactions,
        payment_methods: paymentMethods,
        sales_channels_cash: salesChannelsCashArray,
        sales_channels_all: salesChannelsAll,
        
        bonus_system: {
          points_awarded: 0,
          points_redeemed: 0,
          amount: 0
        },
        
        created_at: now.toISOString()
      };
      
      console.log(`✅ [ZWISCHENBERICHT] Zwischenbericht generated successfully`);
      return zwischenbericht as Zwischenbericht;
      
    } catch (error) {
      console.error(`❌ [ZWISCHENBERICHT] Failed to generate Zwischenbericht:`, error);
      throw error;
    }
  }

  /**
   * Generate interim sales summary for real-time tracking
   */
  public async generateInterimSalesSummary(
    cashierName: string,
    periodHours: number = 1
  ): Promise<InterimSalesSummary> {
    try {
      console.log(`📊 [INTERIM SALES] Generating interim sales summary...`);
      
      const now = new Date();
      const periodStart = new Date(now.getTime() - (periodHours * 60 * 60 * 1000));
      
      // Fetch recent data - FILTERED BY CASHIER
      // CRITICAL: Only get orders/payments created by this specific cashier
      const [orders, payments] = await Promise.all([
        this.getOrdersForPeriod(periodStart.toISOString(), now.toISOString(), cashierName),
        this.getPaymentsForPeriod(periodStart.toISOString(), now.toISOString(), cashierName)
      ]);
      
      console.log(`📊 [INTERIM SALES] Filtered data for cashier "${cashierName}": ${orders.length} orders, ${payments.length} payments`);

      const summary: Omit<InterimSalesSummary, 'id'> = {
        summary_time: now.toISOString(),
        period_start: periodStart.toISOString(),
        period_end: now.toISOString(),
        cashier_name: cashierName,
        
        quick_summary: this.generateQuickSummary(orders, payments, periodHours),
        recent_activity: this.generateRecentActivity(orders, payments),
        alerts: this.generateAlerts(orders, payments),
        
        created_at: now.toISOString()
      };

      // Save interim summary
      const savedSummary = await this.saveInterimSalesSummary(summary);
      
      console.log(`✅ [INTERIM SALES] Interim sales summary generated successfully`);
      return savedSummary;

    } catch (error) {
      console.error(`❌ [INTERIM SALES] Failed to generate interim sales summary:`, error);
      throw error;
    }
  }

  /**
   * Get all daily closing reports
   */
  public async getDailyClosingReports(): Promise<DailyClosingReport[]> {
    try {
      console.log('📊 [DAILY CLOSING] Fetching daily closing reports...');
      
      const { collection, getDocs, orderBy, query } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      const reportsRef = collection(db, 'daily_closing_reports');
      const q = query(reportsRef, orderBy('report_date', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const reports: DailyClosingReport[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        reports.push({
          id: doc.id,
          ...data,
          created_at: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          updated_at: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
        } as DailyClosingReport);
      });

      console.log(`✅ [DAILY CLOSING] Fetched ${reports.length} daily closing reports`);
      return reports;

    } catch (error) {
      console.error('❌ [DAILY CLOSING] Failed to fetch daily closing reports:', error);
      throw error;
    }
  }

  /**
   * Generate financial summary
   */
  private generateFinancialSummary(orders: any[], payments: any[]): DailyClosingReport['financial_summary'] {
    // Single source of truth: shared revenue filter. Excludes cancelled,
    // refunded, voided, and orders fully refunded by amount. Identical to
    // POS Orders + Admin Reports + Branches.
    const completedOrders = orders.filter(isRevenueOrder);

    const totalSales = completedOrders.reduce((sum, order) => {
      const orderTotal = typeof order.total === 'number' ? order.total : parseFloat(order.total || '0');
      return sum + (isNaN(orderTotal) ? 0 : orderTotal);
    }, 0);
    const totalOrders = completedOrders.length;
    const totalCustomers = new Set(completedOrders.map(order => order.customer_name).filter(Boolean)).size;
    const averageOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;

    // FINANZAMT-CRITICAL: tax must be split per German rate (19% dine-in,
    // 7% takeaway/delivery), not a flat 19% on every order. Previous flat
    // calc over-stated VAT collected on takeaway by ~12% of net.
    const vat = aggregateVAT(completedOrders);
    const netRevenue = Math.round(vat.netRevenue * 100) / 100;
    const taxCollected = Math.round(vat.taxCollected * 100) / 100;
    
    // Calculate discounts and refunds
    const discountsGiven = completedOrders.reduce((sum, order) => sum + (order.discount_amount || 0), 0);
    const refundsIssued = payments.reduce((sum, payment) => sum + (payment.refund_amount || 0), 0);
    
    // Split payment statistics
    const splitPaymentOrders = completedOrders.filter(order => order.is_split_payment).length;
    const splitPaymentRevenue = completedOrders
      .filter(order => order.is_split_payment)
      .reduce((sum, order) => sum + (order.total || 0), 0);
    const splitPaymentPercentage = totalOrders > 0 ? (splitPaymentOrders / totalOrders) * 100 : 0;

    return {
      total_sales: totalSales,
      total_orders: totalOrders,
      total_customers: totalCustomers,
      average_order_value: averageOrderValue,
      gross_revenue: totalSales,
      net_revenue: netRevenue,
      tax_collected: taxCollected,
      discounts_given: discountsGiven,
      refunds_issued: refundsIssued,
      split_payment_orders: splitPaymentOrders,
      split_payment_revenue: splitPaymentRevenue,
      split_payment_percentage: splitPaymentPercentage
    };
  }

  /**
   * Generate payment methods breakdown
   */
  private generatePaymentMethodsBreakdown(payments: any[]): DailyClosingReport['payment_methods'] {
    const completedPayments = payments.filter(payment => payment.status === 'completed');
    
    const methods: Record<string, { count: number; amount: number }> = {
      cash: { count: 0, amount: 0 },
      card: { count: 0, amount: 0 },
      paypal: { count: 0, amount: 0 },
      other: { count: 0, amount: 0 }
    };

    completedPayments.forEach(payment => {
      const method = payment.payment_method?.toLowerCase() || 'other';
      const amount = payment.amount || 0;
      
      if (methods[method]) {
        methods[method].count++;
        methods[method].amount += amount;
      } else {
        methods.other.count++;
        methods.other.amount += amount;
      }
    });

    const totalAmount = Object.values(methods).reduce((sum, method) => sum + method.amount, 0);

    return {
      cash: {
        ...methods.cash,
        percentage: totalAmount > 0 ? (methods.cash.amount / totalAmount) * 100 : 0
      },
      card: {
        ...methods.card,
        percentage: totalAmount > 0 ? (methods.card.amount / totalAmount) * 100 : 0
      },
      paypal: {
        ...methods.paypal,
        percentage: totalAmount > 0 ? (methods.paypal.amount / totalAmount) * 100 : 0
      },
      other: {
        ...methods.other,
        percentage: totalAmount > 0 ? (methods.other.amount / totalAmount) * 100 : 0
      }
    };
  }

  /**
   * Generate cash reconciliation
   */
  private generateCashReconciliation(
    _orders: any[], 
    payments: any[], 
    openingCash: number, 
    actualCash: number
  ): DailyClosingReport['cash_reconciliation'] {
    const cashPayments = payments.filter(payment => 
      payment.payment_method?.toLowerCase() === 'cash' && payment.status === 'completed'
    );
    
    const cashRefunds = payments.filter(payment => 
      payment.payment_method?.toLowerCase() === 'cash' && payment.refund_amount > 0
    );

    const cashSales = cashPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    const cashRefundAmount = cashRefunds.reduce((sum, payment) => sum + (payment.refund_amount || 0), 0);
    
    const expectedCash = openingCash + cashSales - cashRefundAmount;
    const cashDifference = actualCash - expectedCash;

    return {
      opening_cash: openingCash,
      cash_sales: cashSales,
      cash_refunds: cashRefundAmount,
      expected_cash: expectedCash,
      actual_cash: actualCash,
      cash_difference: cashDifference,
      cash_over_short: Math.abs(cashDifference)
    };
  }

  /**
   * Generate sales analysis
   */
  private generateSalesAnalysis(orders: any[]): DailyClosingReport['sales_analysis'] {
    // CRITICAL: Filter out cancelled orders and pending payments
    // Include all orders that are not cancelled and have payment completed
    // This matches the logic used in reports page for accurate calculations
    const completedOrders = orders.filter(order => {
      const orderStatus = order.status || 'pending';
      const paymentStatus = order.payment_status || 'pending';
      
      // Include orders that are not cancelled and have payment completed
      return orderStatus !== 'cancelled' && paymentStatus !== 'pending';
    });

    // Hourly breakdown
    const hourlyData: Record<number, { orders: number; revenue: number }> = {};
    for (let hour = 0; hour < 24; hour++) {
      hourlyData[hour] = { orders: 0, revenue: 0 };
    }

    completedOrders.forEach(order => {
      const orderDate = new Date(order.created_at);
      const hour = orderDate.getHours();
      hourlyData[hour].orders++;
      hourlyData[hour].revenue += order.total || 0;
    });

    const hourlyBreakdown = Object.entries(hourlyData).map(([hour, data]) => ({
      hour: parseInt(hour),
      orders: data.orders,
      revenue: data.revenue,
      average_order_value: data.orders > 0 ? data.revenue / data.orders : 0
    }));

    // Top selling items
    const itemCounts: Record<string, { quantity: number; revenue: number }> = {};
    completedOrders.forEach(order => {
      if (order.items) {
        order.items.forEach((item: any) => {
          const itemName = item.menu_item_name || item.name || 'Unknown Item';
          if (!itemCounts[itemName]) {
            itemCounts[itemName] = { quantity: 0, revenue: 0 };
          }
          itemCounts[itemName].quantity += item.quantity || 0;
          itemCounts[itemName].revenue += item.total || 0;
        });
      }
    });

    const totalRevenue = completedOrders.reduce((sum, order) => sum + (order.total || 0), 0);
    const topSellingItems = Object.entries(itemCounts)
      .map(([name, data]) => ({
        name,
        quantity: data.quantity,
        revenue: data.revenue,
        percentage_of_sales: totalRevenue > 0 ? (data.revenue / totalRevenue) * 100 : 0
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);

    // Category performance (simplified)
    const categoryPerformance = [
      { category: 'Sushi', orders: Math.floor(completedOrders.length * 0.4), revenue: totalRevenue * 0.4, percentage_of_sales: 40 },
      { category: 'Pizza', orders: Math.floor(completedOrders.length * 0.3), revenue: totalRevenue * 0.3, percentage_of_sales: 30 },
      { category: 'Pasta', orders: Math.floor(completedOrders.length * 0.2), revenue: totalRevenue * 0.2, percentage_of_sales: 20 },
      { category: 'Other', orders: Math.floor(completedOrders.length * 0.1), revenue: totalRevenue * 0.1, percentage_of_sales: 10 }
    ];

    return {
      hourly_breakdown: hourlyBreakdown,
      top_selling_items: topSellingItems,
      category_performance: categoryPerformance
    };
  }

  /**
   * Generate operational metrics
   */
  private generateOperationalMetrics(orders: any[]): DailyClosingReport['operational_metrics'] {
    // CRITICAL: Filter out cancelled orders and pending payments
    // Include all orders that are not cancelled and have payment completed
    // This matches the logic used in reports page for accurate calculations
    const completedOrders = orders.filter(order => {
      const orderStatus = order.status || 'pending';
      const paymentStatus = order.payment_status || 'pending';
      
      // Include orders that are not cancelled and have payment completed
      return orderStatus !== 'cancelled' && paymentStatus !== 'pending';
    });

    // Find peak and slow hours
    const hourlyOrders: Record<number, number> = {};
    completedOrders.forEach(order => {
      const hour = new Date(order.created_at).getHours();
      hourlyOrders[hour] = (hourlyOrders[hour] || 0) + 1;
    });

    const sortedHours = Object.entries(hourlyOrders)
      .sort(([,a], [,b]) => b - a)
      .map(([hour]) => `${hour}:00`);

    const peakHours = sortedHours.slice(0, 3);
    const slowHours = sortedHours.slice(-3);

    // Calculate average service time (simplified)
    const averageServiceTime = completedOrders.length > 0 ? 15 : 0; // 15 minutes average

    return {
      peak_hours: peakHours,
      slow_hours: slowHours,
      busiest_staff_member: 'Cashier 1', // This would need staff tracking
      average_service_time: averageServiceTime,
      customer_satisfaction_score: 4.5 // This would need feedback system
    };
  }

  /**
   * Generate employee performance breakdown
   * NEW: Tracks each employee separately with their orders, sales, and hours worked
   */
  private generateEmployeePerformance(orders: any[], payments: any[]): DailyClosingReport['employee_performance'] {
    // Group orders by employee
    const employeeMap: Record<string, {
      employee_name: string;
      employee_email: string;
      orders: any[];
      orderTimes: Date[];
    }> = {};

    // CRITICAL: Filter out cancelled orders and pending payments
    const completedOrders = orders.filter(order => {
      const orderStatus = order.status || 'pending';
      const paymentStatus = order.payment_status || 'pending';
      return orderStatus !== 'cancelled' && paymentStatus !== 'pending';
    });

    completedOrders.forEach(order => {
      // Use created_by_email as primary key, fallback to created_by_name or created_by
      const employeeEmail = order.created_by_email || order.created_by || 'Unknown Employee';
      const employeeName = order.created_by_name || order.created_by || employeeEmail;
      
      if (!employeeMap[employeeEmail]) {
        employeeMap[employeeEmail] = {
          employee_name: employeeName,
          employee_email: employeeEmail,
          orders: [],
          orderTimes: []
        };
      }
      
      employeeMap[employeeEmail].orders.push(order);
      
      // Track order times for hours calculation
      const orderTime = new Date(order.created_at);
      if (!isNaN(orderTime.getTime())) {
        employeeMap[employeeEmail].orderTimes.push(orderTime);
      }
    });

    // Create payment map for employee-specific payment methods
    const paymentMap: Record<string, any[]> = {};
    const completedPayments = payments.filter(p => p.status === 'completed');
    
    completedPayments.forEach(payment => {
      // Match payment to order by order_id
      const order = completedOrders.find(o => o.id === payment.order_id || o.order_number === payment.order_number);
      if (order) {
        const employeeEmail = order.created_by_email || order.created_by || 'Unknown Employee';
        if (!paymentMap[employeeEmail]) {
          paymentMap[employeeEmail] = [];
        }
        paymentMap[employeeEmail].push(payment);
      }
    });

    // Calculate performance for each employee
    const employeePerformance = Object.values(employeeMap).map(employee => {
      const employeeOrders = employee.orders;
      const employeePayments = paymentMap[employee.employee_email] || [];
      
      // Calculate total sales from orders
      const totalSales = employeeOrders.reduce((sum, order) => {
        const orderTotal = typeof order.total === 'number' ? order.total : parseFloat(order.total || '0');
        return sum + (isNaN(orderTotal) ? 0 : orderTotal);
      }, 0);
      
      // Calculate hours worked
      let hoursWorked = 0;
      if (employee.orderTimes.length > 0) {
        const firstOrder = new Date(Math.min(...employee.orderTimes.map(t => t.getTime())));
        const lastOrder = new Date(Math.max(...employee.orderTimes.map(t => t.getTime())));
        const timeDiff = lastOrder.getTime() - firstOrder.getTime();
        hoursWorked = Math.max(0.5, timeDiff / (1000 * 60 * 60)); // Minimum 0.5 hours, convert ms to hours
      }
      
      // Calculate payment methods breakdown for this employee
      const paymentMethods = {
        cash: 0,
        card: 0,
        paypal: 0,
        other: 0
      };
      
      employeePayments.forEach(payment => {
        const method = (payment.payment_method || 'other').toLowerCase();
        const amount = payment.amount || 0;
        if (paymentMethods[method as keyof typeof paymentMethods] !== undefined) {
          paymentMethods[method as keyof typeof paymentMethods] += amount;
        } else {
          paymentMethods.other += amount;
        }
      });
      
      return {
        employee_name: employee.employee_name,
        employee_email: employee.employee_email,
        orders_created: employeeOrders.length,
        total_sales: totalSales,
        average_order_value: employeeOrders.length > 0 ? totalSales / employeeOrders.length : 0,
        hours_worked: Math.round(hoursWorked * 10) / 10, // Round to 1 decimal place
        first_order_time: employee.orderTimes.length > 0 
          ? new Date(Math.min(...employee.orderTimes.map(t => t.getTime()))).toISOString()
          : '',
        last_order_time: employee.orderTimes.length > 0
          ? new Date(Math.max(...employee.orderTimes.map(t => t.getTime()))).toISOString()
          : '',
        payment_methods: paymentMethods
      };
    });

    // Sort by total sales (descending)
    return employeePerformance.sort((a, b) => b.total_sales - a.total_sales);
  }

  /**
   * Generate audit trail
   */
  private generateAuditTrail(orders: any[]): DailyClosingReport['audit_trail'] {
    const orderTimes = orders.map(order => new Date(order.created_at));
    const firstOrderTime = orderTimes.length > 0 ? Math.min(...orderTimes.map(t => t.getTime())) : 0;
    const lastOrderTime = orderTimes.length > 0 ? Math.max(...orderTimes.map(t => t.getTime())) : 0;

    return {
      report_generated_at: new Date().toISOString(),
      generated_by: 'POS System',
      last_order_time: lastOrderTime > 0 ? new Date(lastOrderTime).toISOString() : '',
      first_order_time: firstOrderTime > 0 ? new Date(firstOrderTime).toISOString() : '',
      system_uptime: 24 * 60 * 60, // 24 hours in seconds
      errors_logged: 0
    };
  }

  /**
   * Generate quick summary for interim reports
   */
  private generateQuickSummary(orders: any[], _payments: any[], periodHours: number): InterimSalesSummary['quick_summary'] {
    // CRITICAL: Filter out cancelled orders and pending payments
    // Include all orders that are not cancelled and have payment completed
    // This matches the logic used in reports page for accurate calculations
    const completedOrders = orders.filter(order => {
      const orderStatus = order.status || 'pending';
      const paymentStatus = order.payment_status || 'pending';
      
      // Include orders that are not cancelled and have payment completed
      return orderStatus !== 'cancelled' && paymentStatus !== 'pending';
    });
    
    // CRITICAL FIX: Use orders as the source of truth for total sales (same as Daily Closing Reports)
    // Orders are the primary source - payments may be incomplete or missing
    // This ensures accurate calculation showing ALL completed orders for the period
    const currentSales = completedOrders.reduce((sum, order) => {
      const orderTotal = typeof order.total === 'number' ? order.total : parseFloat(order.total || '0');
      return sum + (isNaN(orderTotal) ? 0 : orderTotal);
    }, 0);
    
    const currentOrders = completedOrders.length;
    const hourlyRate = periodHours > 0 ? currentSales / periodHours : 0;
    
    // Project daily sales based on current rate
    const projectedDailySales = hourlyRate * 12; // Assuming 12-hour operation
    
    // Calculate time to reach daily goal (assuming €1000 goal)
    const dailyGoal = 1000;
    const timeToGoal = hourlyRate > 0 ? `${Math.ceil((dailyGoal - currentSales) / hourlyRate)} hours` : 'N/A';

    console.log(`📊 [INTERIM SALES] Quick Summary: ${currentOrders} orders, €${currentSales.toFixed(2)} sales, €${hourlyRate.toFixed(2)}/hour`);

    return {
      current_sales: Math.round(currentSales * 100) / 100, // Round to 2 decimal places
      current_orders: currentOrders,
      hourly_rate: Math.round(hourlyRate * 100) / 100, // Round to 2 decimal places
      projected_daily_sales: Math.round(projectedDailySales * 100) / 100, // Round to 2 decimal places
      time_to_goal: timeToGoal
    };
  }

  /**
   * Generate recent activity
   */
  private generateRecentActivity(orders: any[], payments: any[]): InterimSalesSummary['recent_activity'] {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - (60 * 60 * 1000));
    
    // CRITICAL: Filter out cancelled orders and pending payments
    const recentOrders = orders.filter(order => {
      const orderStatus = order.status || 'pending';
      const paymentStatus = order.payment_status || 'pending';
      const isRecent = new Date(order.created_at) >= oneHourAgo;
      
      // Only include recent completed/delivered orders with completed payment
      return isRecent &&
             (orderStatus === 'completed' || orderStatus === 'delivered') &&
             paymentStatus === 'completed' &&
             orderStatus !== 'cancelled';
    });
    
    const recentPayments = payments.filter(payment => 
      new Date(payment.created_at) >= oneHourAgo && payment.status === 'completed'
    );

    const lastHourOrders = recentOrders.length;
    const lastHourRevenue = recentPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    
    // Find peak performance hour
    const hourlyData: Record<number, number> = {};
    recentOrders.forEach(order => {
      const hour = new Date(order.created_at).getHours();
      hourlyData[hour] = (hourlyData[hour] || 0) + 1;
    });
    
    const peakHour = Object.entries(hourlyData)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || '12';
    
    const peakPerformanceHour = `${peakHour}:00`;

    return {
      last_hour_orders: lastHourOrders,
      last_hour_revenue: lastHourRevenue,
      peak_performance_hour: peakPerformanceHour,
      current_trend: lastHourOrders > 5 ? 'increasing' : lastHourOrders < 2 ? 'decreasing' : 'stable'
    };
  }

  /**
   * Generate alerts
   */
  private generateAlerts(orders: any[], payments: any[]): InterimSalesSummary['alerts'] {
    const alerts: InterimSalesSummary['alerts'] = [];
    const now = new Date();
    
    // Check for low sales alert
    // CRITICAL: Filter out cancelled orders and pending payments
    const recentOrders = orders.filter(order => {
      const orderStatus = order.status || 'pending';
      const paymentStatus = order.payment_status || 'pending';
      const isRecent = new Date(order.created_at) >= new Date(now.getTime() - (2 * 60 * 60 * 1000));
      
      // Only include recent completed/delivered orders with completed payment
      return isRecent &&
             (orderStatus === 'completed' || orderStatus === 'delivered') &&
             paymentStatus === 'completed' &&
             orderStatus !== 'cancelled';
    });
    
    if (recentOrders.length < 3) {
      alerts.push({
        type: 'warning',
        message: 'Low sales activity in the last 2 hours',
        timestamp: now.toISOString()
      });
    }
    
    // Check for high sales alert
    if (recentOrders.length > 15) {
      alerts.push({
        type: 'success',
        message: 'High sales activity detected!',
        timestamp: now.toISOString()
      });
    }
    
    // Check for payment issues
    const failedPayments = payments.filter(payment => payment.status === 'failed');
    if (failedPayments.length > 0) {
      alerts.push({
        type: 'error',
        message: `${failedPayments.length} failed payment(s) detected`,
        timestamp: now.toISOString()
      });
    }

    return alerts;
  }

  /**
   * Get orders for specific date - FILTERED BY CASHIER
   * CRITICAL: Only returns orders created by the specified cashier
   */
  private async getOrdersForDate(date: string, cashierName: string): Promise<any[]> {
    try {
      const { collection, getDocs, query, where, orderBy } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      // Parse date string "YYYY-MM-DD" as LOCAL date (not UTC)
      const [_y, _m, _d] = date.split('-').map(Number);
      const startOfDay = new Date(_y, _m - 1, _d, 0, 0, 0, 0);
      const endOfDay = new Date(_y, _m - 1, _d, 23, 59, 59, 999);
      
      const ordersRef = collection(db, 'orders');
      const q = query(
        ordersRef,
        where('created_at', '>=', startOfDay),
        where('created_at', '<=', endOfDay),
        orderBy('created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      // DIAGNOSTIC: Log ALL orders found for this date (before filtering)
      console.log(`📊 [DAILY CLOSING] Found ${querySnapshot.size} total orders for date ${date}`);
      const allOrdersForDate: any[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        allOrdersForDate.push({
          id: doc.id,
          order_number: data.order_number || 'no-number',
          total: data.total || 0,
          created_by_email: data.created_by_email || '(empty)',
          created_by_name: data.created_by_name || '(empty)',
          created_by: data.created_by || '(empty)',
          status: data.status || 'unknown',
          payment_status: data.payment_status || 'unknown',
          created_at: data.created_at?.toDate?.()?.toISOString() || data.created_at || 'unknown'
        });
      });
      console.log(`📋 [DAILY CLOSING] All orders for ${date}:`, allOrdersForDate);
      
      const orders: any[] = [];

      // AUTO-GENERATED: Skip cashier filtering — include ALL orders for the date
      // Admin users and auto-generated reports include ALL orders (no cashier filter)
      const isAdmin = cashierName.toLowerCase().includes('admin');
      const isAutoGenerated = cashierName === 'System (Auto-Generated)';
      if (isAutoGenerated || isAdmin) {
        console.log(`📊 [DAILY CLOSING] ${isAdmin ? 'Admin' : 'Auto-generated'} report — including ALL ${querySnapshot.size} orders (no cashier filter)`);
        querySnapshot.forEach((doc) => {
          const data = { id: doc.id, ...doc.data() };
          // Shared revenue filter — same one used by POS Orders, Admin
          // Reports, Branches. Single source of truth in reportingUtils.ts.
          if (isRevenueOrder(data)) {
            orders.push(data);
          }
        });
        console.log(`✅ [DAILY CLOSING] Auto-generated: ${orders.length} orders included`);
        return orders;
      }

      // Normalize cashier name for comparison (handle email, name, or both)
      const cashierNameLower = cashierName.toLowerCase().trim();
      const cashierEmail = cashierNameLower.includes('@') ? cashierNameLower : null;
      const cashierNameOnly = cashierEmail ? cashierEmail.split('@')[0] : cashierNameLower;
      
      // Extract all possible identifiers from cashier name
      // If cashier is "irfan@saheehpos.com", we want to match:
      // - "irfan@saheehpos.com" (exact email)
      // - "irfan" (name part)
      // - "Irfan", "IRFAN", etc. (case variations)
      const cashierIdentifiers = new Set<string>();
      cashierIdentifiers.add(cashierNameLower);
      if (cashierEmail) {
        cashierIdentifiers.add(cashierEmail);
        cashierIdentifiers.add(cashierEmail.split('@')[0]); // "irfan" from "irfan@saheehpos.com"
      }
      cashierIdentifiers.add(cashierNameOnly);
      // Add name parts (split by dots, spaces, etc.)
      cashierNameLower.split(/[@.\s_-]+/).forEach(part => {
        if (part.length > 0) {
          cashierIdentifiers.add(part);
        }
      });
      
      console.log(`🔍 [DAILY CLOSING] Matching orders for cashier "${cashierName}" with identifiers:`, Array.from(cashierIdentifiers));
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // CRITICAL: Filter by cashier - check created_by_email, created_by_name, or created_by
        const orderCreatedByEmail = (data.created_by_email || '').toLowerCase().trim();
        const orderCreatedByName = (data.created_by_name || '').toLowerCase().trim();
        const orderCreatedBy = (data.created_by || '').toLowerCase().trim();
        
        // Collect all order identifiers
        const orderIdentifiers = new Set<string>();
        if (orderCreatedByEmail) orderIdentifiers.add(orderCreatedByEmail);
        if (orderCreatedByName) orderIdentifiers.add(orderCreatedByName);
        if (orderCreatedBy) orderIdentifiers.add(orderCreatedBy);
        // Also add email username part if it's an email
        if (orderCreatedByEmail && orderCreatedByEmail.includes('@')) {
          orderIdentifiers.add(orderCreatedByEmail.split('@')[0]);
        }
        // Add name parts
        [orderCreatedByEmail, orderCreatedByName, orderCreatedBy].forEach((id: string) => {
          if (id) {
            id.split(/[@.\s_-]+/).forEach((part: string) => {
              if (part.length > 0) {
                orderIdentifiers.add(part);
              }
            });
          }
        });
        
        // STRICT MATCHING LOGIC - Only match if there's a clear connection
        // All comparisons are case-insensitive (already lowercased above)
        
        // Priority 1: Exact email match (most reliable)
        const exactEmailMatch = cashierEmail && orderCreatedByEmail && cashierEmail === orderCreatedByEmail;
        
        // Priority 2: Email username match (if cashier is "irfan@saheehpos.com" and order has "irfan" or "Irfan")
        // This handles: cashier="irfan@saheehpos.com" matching order with created_by_name="Irfan" or created_by="irfan"
        const emailUsernameMatch = cashierEmail && cashierNameOnly && (
          orderCreatedByEmail === cashierNameOnly ||
          orderCreatedByName === cashierNameOnly ||
          orderCreatedBy === cashierNameOnly ||
          // Also check if order name contains the username (handles "Irfan" matching "irfan")
          (orderCreatedByName && orderCreatedByName.toLowerCase() === cashierNameOnly) ||
          (orderCreatedBy && orderCreatedBy.toLowerCase() === cashierNameOnly)
        );
        
        // Priority 3: Exact name match (case-insensitive)
        // Handles: cashier="Irfan" matching order with created_by_name="Irfan" or created_by="irfan"
        const exactNameMatch = !cashierEmail && (
          orderCreatedByName === cashierNameLower ||
          orderCreatedBy === cashierNameLower ||
          (orderCreatedByName && orderCreatedByName.toLowerCase() === cashierNameLower) ||
          (orderCreatedBy && orderCreatedBy.toLowerCase() === cashierNameLower)
        );
        
        // Priority 4: Name contains cashier name (only if cashier name is short/single word)
        // This handles cases like cashier="Irfan" matching order created_by="Irfan" or "irfan@saheehpos.com"
        const nameContainsMatch = cashierNameOnly.length <= 20 && (
          (orderCreatedByEmail && orderCreatedByEmail.includes(cashierNameOnly)) ||
          (orderCreatedByName && orderCreatedByName.includes(cashierNameOnly)) ||
          (orderCreatedBy && orderCreatedBy.includes(cashierNameOnly)) ||
          // Case-insensitive contains check
          (orderCreatedByName && orderCreatedByName.toLowerCase().includes(cashierNameOnly)) ||
          (orderCreatedBy && orderCreatedBy.toLowerCase().includes(cashierNameOnly))
        );
        
        // Priority 5: Reverse match - if cashier is just a name (no @), check if order email username matches
        // Example: cashier="irfan" and order has created_by_email="irfan@saheehpos.com"
        const reverseEmailMatch = !cashierEmail && orderCreatedByEmail && orderCreatedByEmail.includes('@') && 
          orderCreatedByEmail.split('@')[0] === cashierNameLower;
        
        // Priority 6: Email username in order name (if cashier is "irfan@saheehpos.com" and order has name containing "irfan")
        // This handles: cashier="irfan@saheehpos.com" matching order with created_by_name="Irfan" (case-insensitive)
        const emailUsernameInName = cashierEmail && cashierNameOnly && (
          (orderCreatedByName && orderCreatedByName.toLowerCase().includes(cashierNameOnly)) ||
          (orderCreatedBy && orderCreatedBy.toLowerCase().includes(cashierNameOnly))
        );
        
        // Only match if one of the strict conditions is met
        const matchesCashier = exactEmailMatch || emailUsernameMatch || exactNameMatch || nameContainsMatch || reverseEmailMatch || emailUsernameInName;
        
        if (!matchesCashier) {
          console.log(`⏭️ [DAILY CLOSING] Excluding order ${doc.id} (${data.order_number || 'no-number'}):`, {
            cashier: cashierName,
            cashierEmail: cashierEmail,
            cashierNameOnly: cashierNameOnly,
            orderCreatedByEmail: orderCreatedByEmail || '(empty)',
            orderCreatedByName: orderCreatedByName || '(empty)',
            orderCreatedBy: orderCreatedBy || '(empty)',
            orderTotal: data.total,
            matchAttempts: {
              exactEmailMatch,
              emailUsernameMatch,
              exactNameMatch,
              nameContainsMatch,
              reverseEmailMatch,
              emailUsernameInName
            }
          });
          return; // Skip this order - not created by this cashier
        }
        
        const matchType = exactEmailMatch ? 'exact-email' : 
                         emailUsernameMatch ? 'email-username' : 
                         exactNameMatch ? 'exact-name' : 
                         reverseEmailMatch ? 'reverse-email' :
                         emailUsernameInName ? 'email-username-in-name' :
                         'name-contains';
        
        console.log(`✅ [DAILY CLOSING] Including order ${doc.id} (${data.order_number || 'no-number'}):`, {
          cashier: cashierName,
          orderCreatedByEmail: orderCreatedByEmail || '(empty)',
          orderCreatedByName: orderCreatedByName || '(empty)',
          orderCreatedBy: orderCreatedBy || '(empty)',
          orderTotal: data.total,
          matchType: matchType
        });
        
        // Shared revenue filter — same logic as POS Orders, Admin Reports,
        // Branches, and the admin/auto path above. Excludes cancelled,
        // refunded, voided, and orders fully refunded by amount.
        if (!isRevenueOrder(data)) {
          console.log(`⏭️ [DAILY CLOSING] Excluding order ${doc.id} (failed revenue filter): status=${data.status}, payment_status=${data.payment_status}`);
          return;
        }

        orders.push({
          id: doc.id,
          ...data,
          created_at: data.created_at?.toDate?.()?.toISOString() ||
                     (data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString())
        });
      });
      
      console.log(`✅ [DAILY CLOSING] Filtered orders for cashier "${cashierName}": ${orders.length} valid orders (excluded cancelled/pending and other cashiers)`);
      return orders;
    } catch (error) {
      console.error('❌ [DAILY CLOSING] Failed to fetch orders for date:', error);
      return [];
    }
  }

  /**
   * Get payments for specific date - FILTERED BY CASHIER
   * CRITICAL: Only returns payments for orders created by the specified cashier
   */
  private async getPaymentsForDate(date: string, cashierName: string): Promise<any[]> {
    try {
      // First get orders for this cashier on this date
      const cashierOrders = await this.getOrdersForDate(date, cashierName);
      const cashierOrderIds = new Set(cashierOrders.map(o => o.id));
      
      const { collection, getDocs, query, where, orderBy } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      // Parse date string "YYYY-MM-DD" as LOCAL date (not UTC)
      const [_y, _m, _d] = date.split('-').map(Number);
      const startOfDay = new Date(_y, _m - 1, _d, 0, 0, 0, 0);
      const endOfDay = new Date(_y, _m - 1, _d, 23, 59, 59, 999);
      
      const paymentsRef = collection(db, 'payments');
      const q = query(
        paymentsRef,
        where('created_at', '>=', startOfDay),
        where('created_at', '<=', endOfDay),
        orderBy('created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const payments: any[] = [];
      
      // Normalize cashier name for comparison
      const cashierNameLower = cashierName.toLowerCase().trim();
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Match payments to orders created by this cashier
        const paymentOrderId = data.order_id || '';
        const paymentProcessedBy = (data.processed_by || '').toLowerCase().trim();
        
        // Include payment if:
        // 1. It's linked to an order created by this cashier, OR
        // 2. The processed_by field matches the cashier
        const matchesCashier = 
          cashierOrderIds.has(paymentOrderId) ||
          paymentProcessedBy === cashierNameLower ||
          (data.created_by_email && data.created_by_email.toLowerCase().trim() === cashierNameLower);
        
        if (!matchesCashier) {
          console.log(`⏭️ [DAILY CLOSING] Excluding payment ${doc.id}: order_id="${paymentOrderId}", processed_by="${paymentProcessedBy}" does not match cashier="${cashierName}"`);
          return; // Skip this payment - not for this cashier's orders
        }
        
        payments.push({
          id: doc.id,
          ...data,
          created_at: data.created_at?.toDate?.()?.toISOString() || 
                     (data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString())
        });
      });
      
      console.log(`✅ [DAILY CLOSING] Filtered payments for cashier "${cashierName}": ${payments.length} valid payments`);
      return payments;
    } catch (error) {
      console.error('❌ [DAILY CLOSING] Failed to fetch payments for date:', error);
      return [];
    }
  }

  /**
   * Get orders for period - FILTERED BY CASHIER
   * CRITICAL: Only returns orders created by the specified cashier
   */
  private async getOrdersForPeriod(startDate: string, endDate: string, cashierName: string): Promise<any[]> {
    try {
      const { collection, getDocs, query, where, orderBy } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      const ordersRef = collection(db, 'orders');
      const q = query(
        ordersRef,
        where('created_at', '>=', new Date(startDate)),
        where('created_at', '<=', new Date(endDate)),
        orderBy('created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const orders: any[] = [];

      // Admin users see ALL orders (no cashier filter)
      // Positive allow-list — interim sales report must only count genuinely
      // billed orders, not pending/preparing ones that haven't closed yet.
      const isAdmin = cashierName.toLowerCase().includes('admin');
      if (isAdmin) {
        const BILLED_STATUSES = ['completed', 'delivered'];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const status = (data.status || '').toLowerCase();
          if (BILLED_STATUSES.includes(status)) {
            orders.push({
              id: doc.id, ...data,
              created_at: data.created_at?.toDate?.()?.toISOString() || (data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString())
            });
          }
        });
        console.log(`📊 [INTERIM] Admin — including ALL ${orders.length} orders (no cashier filter)`);
        return orders;
      }

      // Non-admin: filter by cashier
      const cashierNameLower = cashierName.toLowerCase().trim();
      const cashierEmail = cashierNameLower.includes('@') ? cashierNameLower : null;
      const cashierNameOnly = cashierEmail ? cashierEmail.split('@')[0] : cashierNameLower;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const orderCreatedByEmail = (data.created_by_email || '').toLowerCase().trim();
        const orderCreatedByName = (data.created_by_name || '').toLowerCase().trim();
        const orderCreatedBy = (data.created_by || '').toLowerCase().trim();

        const exactEmailMatch = cashierEmail && orderCreatedByEmail && cashierEmail === orderCreatedByEmail;
        const emailUsernameMatch = cashierEmail && cashierNameOnly && (
          orderCreatedByEmail === cashierNameOnly || orderCreatedByName === cashierNameOnly || orderCreatedBy === cashierNameOnly
        );
        const exactNameMatch = !cashierEmail && (orderCreatedByName === cashierNameLower || orderCreatedBy === cashierNameLower);
        const nameContainsMatch = cashierNameOnly.length <= 20 && (
          (orderCreatedByEmail && orderCreatedByEmail.includes(cashierNameOnly)) ||
          (orderCreatedByName && orderCreatedByName.includes(cashierNameOnly)) ||
          (orderCreatedBy && orderCreatedBy.includes(cashierNameOnly))
        );
        const reverseEmailMatch = !cashierEmail && orderCreatedByEmail && orderCreatedByEmail.includes('@') &&
          orderCreatedByEmail.split('@')[0] === cashierNameLower;
        const emailUsernameInName = cashierEmail && cashierNameOnly && (
          (orderCreatedByName && orderCreatedByName.toLowerCase().includes(cashierNameOnly)) ||
          (orderCreatedBy && orderCreatedBy.toLowerCase().includes(cashierNameOnly))
        );

        const matchesCashier = exactEmailMatch || emailUsernameMatch || exactNameMatch || nameContainsMatch || reverseEmailMatch || emailUsernameInName;

        if (!matchesCashier) return;

        orders.push({
          id: doc.id, ...data,
          created_at: data.created_at?.toDate?.()?.toISOString() || (data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString())
        });
      });
      
      return orders;
    } catch (error) {
      console.error('❌ [DAILY CLOSING] Failed to fetch orders for period:', error);
      return [];
    }
  }

  /**
   * Get payments for period - FILTERED BY CASHIER
   * CRITICAL: Only returns payments for orders created by the specified cashier
   */
  private async getPaymentsForPeriod(startDate: string, endDate: string, cashierName: string): Promise<any[]> {
    try {
      // First get orders for this cashier in this period
      const cashierOrders = await this.getOrdersForPeriod(startDate, endDate, cashierName);
      const cashierOrderIds = new Set(cashierOrders.map(o => o.id));
      
      const { collection, getDocs, query, where, orderBy } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      const paymentsRef = collection(db, 'payments');
      const q = query(
        paymentsRef,
        where('created_at', '>=', new Date(startDate)),
        where('created_at', '<=', new Date(endDate)),
        orderBy('created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const payments: any[] = [];
      
      // Normalize cashier name for comparison
      const cashierNameLower = cashierName.toLowerCase().trim();
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Match payments to orders created by this cashier
        const paymentOrderId = data.order_id || '';
        const paymentProcessedBy = (data.processed_by || '').toLowerCase().trim();
        
        const matchesCashier = 
          cashierOrderIds.has(paymentOrderId) ||
          paymentProcessedBy === cashierNameLower ||
          (data.created_by_email && data.created_by_email.toLowerCase().trim() === cashierNameLower);
        
        if (!matchesCashier) {
          return; // Skip this payment - not for this cashier's orders
        }
        
        payments.push({
          id: doc.id,
          ...data,
          created_at: data.created_at?.toDate?.()?.toISOString() || 
                     (data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString())
        });
      });
      
      return payments;
    } catch (error) {
      console.error('❌ [DAILY CLOSING] Failed to fetch payments for period:', error);
      return [];
    }
  }

  /**
   * Save daily closing report to Firebase
   */
  private async saveDailyClosingReport(report: Omit<DailyClosingReport, 'id'>): Promise<DailyClosingReport> {
    try {
      const { collection, addDoc, serverTimestamp, query: fsQuery, where: fsWhere, getDocs: fsGetDocs, updateDoc, doc: fsDoc } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');

      // Check for existing report on same date + same cashier to prevent duplicates
      const reportsRef = collection(db, 'daily_closing_reports');
      const existingQ = fsQuery(reportsRef,
        fsWhere('report_date', '==', report.report_date),
        fsWhere('cashier_name', '==', report.cashier_name)
      );
      const existingSnap = await fsGetDocs(existingQ);

      if (!existingSnap.empty) {
        // Update existing report instead of creating duplicate
        const existingDoc = existingSnap.docs[0];
        console.log(`🔄 [DAILY CLOSING] Updating existing report ${existingDoc.id} for ${report.report_date}`);
        await updateDoc(fsDoc(db, 'daily_closing_reports', existingDoc.id), {
          ...report,
          updatedAt: serverTimestamp()
        });
        return { ...report, id: existingDoc.id };
      }

      const docRef = await addDoc(collection(db, 'daily_closing_reports'), {
        ...report,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      const savedReport: DailyClosingReport = {
        ...report,
        id: docRef.id
      };

      console.log('✅ [DAILY CLOSING] Daily closing report saved successfully');
      return savedReport;

    } catch (error) {
      console.error('❌ [DAILY CLOSING] Failed to save daily closing report:', error);
      throw error;
    }
  }

  /**
   * Save interim sales summary to Firebase
   */
  private async saveInterimSalesSummary(summary: Omit<InterimSalesSummary, 'id'>): Promise<InterimSalesSummary> {
    try {
      const { collection, addDoc, serverTimestamp } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      const docRef = await addDoc(collection(db, 'interim_sales_summaries'), {
        ...summary,
        createdAt: serverTimestamp()
      });

      const savedSummary: InterimSalesSummary = {
        ...summary,
        id: docRef.id
      };

      console.log('✅ [INTERIM SALES] Interim sales summary saved successfully');
      return savedSummary;

    } catch (error) {
      console.error('❌ [INTERIM SALES] Failed to save interim sales summary:', error);
      throw error;
    }
  }

  /**
   * Delete a daily closing report
   */
  public async deleteDailyClosingReport(reportId: string): Promise<void> {
    try {
      console.log(`🗑️ [DAILY CLOSING] Deleting daily closing report: ${reportId}`);
      
      const { doc, deleteDoc } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      const reportRef = doc(db, 'daily_closing_reports', reportId);
      await deleteDoc(reportRef);
      
      console.log(`✅ [DAILY CLOSING] Daily closing report deleted successfully: ${reportId}`);
    } catch (error) {
      console.error(`❌ [DAILY CLOSING] Failed to delete daily closing report:`, error);
      throw error;
    }
  }

  /**
   * Reset a daily closing report:
   * - Logs reset event to dedicated collection with reason and staff member
   * - Deletes the original report so it can be regenerated
   */
  public async resetDailyClosingReport(
    reportId: string,
    reason: string,
    performedBy: { name: string; email?: string }
  ): Promise<void> {
    try {
      console.log(`♻️ [DAILY CLOSING] Resetting daily closing report: ${reportId}`);

      const { doc, getDoc, collection, addDoc, serverTimestamp, deleteDoc } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');

      const reportRef = doc(db, 'daily_closing_reports', reportId);
      const snapshot = await getDoc(reportRef);

      if (!snapshot.exists()) {
        throw new Error('Daily closing report not found');
      }

      const data = snapshot.data() as any;

      // Try to log reset event for admin visibility (non-critical, continue if it fails)
      try {
        const resetsRef = collection(db, 'daily_closing_resets');
        await addDoc(resetsRef, {
          report_id: reportId,
          report_date: data.report_date || null,
          cashier_name: data.cashier_name || '',
          branch: data.branch || '',
          previous_status: data.status || 'unknown',
          performed_by_name: performedBy.name,
          performed_by_email: performedBy.email || '',
          reason,
          createdAt: serverTimestamp(),
        });
        console.log(`✅ [DAILY CLOSING] Reset event logged successfully`);
      } catch (logError: any) {
        // Log error but continue with deletion (logging is non-critical)
        console.warn(`⚠️ [DAILY CLOSING] Failed to log reset event (continuing with deletion):`, logError);
        // If it's a permissions error, provide helpful message
        if (logError?.code === 'permission-denied' || logError?.message?.includes('permission')) {
          console.warn(`⚠️ [DAILY CLOSING] Permission denied for daily_closing_resets collection. Please add security rules.`);
        }
      }

      // Delete the original report so it can be regenerated (this is the critical action)
      await deleteDoc(reportRef);

      console.log(`✅ [DAILY CLOSING] Report deleted successfully: ${reportId}`);
    } catch (error) {
      console.error('❌ [DAILY CLOSING] Failed to reset daily closing report:', error);
      throw error;
    }
  }

  /**
   * Delete all reports for a specific cashier
   */
  public async deleteReportsByCashier(cashierName: string): Promise<number> {
    try {
      console.log(`🗑️ [DAILY CLOSING] Deleting all reports for cashier: ${cashierName}`);
      
      const { collection, getDocs, query, where, deleteDoc, doc } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');
      
      const reportsRef = collection(db, 'daily_closing_reports');
      const q = query(reportsRef, where('cashier_name', '==', cashierName));
      const querySnapshot = await getDocs(q);
      
      let deletedCount = 0;
      const deletePromises = querySnapshot.docs.map(async (docSnapshot) => {
        await deleteDoc(doc(db, 'daily_closing_reports', docSnapshot.id));
        deletedCount++;
      });
      
      await Promise.all(deletePromises);
      
      console.log(`✅ [DAILY CLOSING] Deleted ${deletedCount} reports for cashier: ${cashierName}`);
      return deletedCount;
    } catch (error) {
      console.error(`❌ [DAILY CLOSING] Failed to delete reports by cashier:`, error);
      throw error;
    }
  }
  /**
   * Auto Z-Report: Check if yesterday's report exists, generate if missing.
   * Should be called on app load. Only generates once per day.
   * Runs at or after 07:00 for the previous day.
   */
  public async autoGenerateZReportIfMissing(): Promise<{ generated: boolean; date?: string; reason?: string }> {
    try {
      const now = new Date();
      const currentHour = now.getHours();

      // Only run at 06:00 or later (business day starts at 6 AM)
      if (currentHour < 6) {
        return { generated: false, reason: 'Before 06:00, skipping auto Z-report check' };
      }

      // Check if we already ran today (use localStorage to prevent multiple runs)
      const lastAutoCheck = localStorage.getItem('auto_zreport_last_check');
      const todayStr = now.toISOString().split('T')[0];
      if (lastAutoCheck === todayStr) {
        return { generated: false, reason: 'Already checked today' };
      }

      // Calculate yesterday's date
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];

      console.log(`📊 [AUTO Z-REPORT] Checking if Z-report exists for ${yesterdayStr}...`);

      // Check if report already exists for yesterday
      const { collection: firestoreCollection, query: firestoreQuery, where: firestoreWhere, getDocs } = await import('../services/firestoreCompat');
      const { db } = await import('./firebaseConfig');

      const reportsRef = firestoreCollection(db, 'daily_closing_reports');
      const q = firestoreQuery(reportsRef, firestoreWhere('report_date', '==', yesterdayStr));
      const snapshot = await getDocs(q);

      // Mark that we checked today (even if we generate or not)
      localStorage.setItem('auto_zreport_last_check', todayStr);

      if (!snapshot.empty) {
        console.log(`✅ [AUTO Z-REPORT] Report for ${yesterdayStr} already exists (${snapshot.size} report(s)). Skipping.`);
        return { generated: false, date: yesterdayStr, reason: 'Report already exists' };
      }

      // No report found — check if there are orders first
      console.log(`⚠️ [AUTO Z-REPORT] No report found for ${yesterdayStr}. Checking for orders...`);

      // Quick check: are there any orders for yesterday?
      const [_yr, _mo, _dy] = yesterdayStr.split('-').map(Number);
      const checkStart = new Date(_yr, _mo - 1, _dy, 0, 0, 0, 0);
      const checkEnd = new Date(_yr, _mo - 1, _dy, 23, 59, 59, 999);
      const { collection: checkCol, query: checkQ, where: checkW, getDocs: checkGet, orderBy: checkOrd, limit: checkLim } = await import('../services/firestoreCompat');
      const checkSnap = await checkGet(checkQ(
        checkCol(db, 'orders'),
        checkW('created_at', '>=', checkStart),
        checkW('created_at', '<=', checkEnd),
        checkOrd('created_at', 'desc'),
        checkLim(1)
      ));

      if (checkSnap.empty) {
        console.log(`ℹ️ [AUTO Z-REPORT] No orders found for ${yesterdayStr}. Skipping report generation.`);
        return { generated: false, date: yesterdayStr, reason: 'No orders for this date' };
      }

      console.log(`📊 [AUTO Z-REPORT] Orders found for ${yesterdayStr}. Auto-generating report...`);

      const report = await this.generateDailyClosingReport(
        yesterdayStr,
        'System (Auto-Generated)',
        0, // opening cash unknown
        0  // actual cash unknown
      );

      // Mark as auto-generated
      if (report.id) {
        const { doc, updateDoc } = await import('../services/firestoreCompat');
        await updateDoc(doc(db, 'daily_closing_reports', report.id), {
          auto_generated: true,
          auto_generated_at: new Date().toISOString(),
          status: 'draft', // Auto-reports are drafts — cashier should review and finalize
          notes: `Automatisch erstellt am ${todayStr} um ${now.toLocaleTimeString('en-US')} (Tagesabschluss für ${yesterdayStr} wurde nicht manuell durchgeführt)`
        });
      }

      console.log(`✅ [AUTO Z-REPORT] Auto-generated Z-report for ${yesterdayStr}`);
      return { generated: true, date: yesterdayStr };

    } catch (error) {
      console.error('❌ [AUTO Z-REPORT] Failed to auto-generate Z-report:', error);
      return { generated: false, reason: `Error: ${(error as any).message}` };
    }
  }
}

export default DailyClosingReportsService;
